/**
 * Utility functions for parsing and formatting field validations
 */

/**
 * Format validation type for display
 * @param {string} type - Validation type
 * @returns {string} - Formatted type
 */
export const formatValidationType = (type) => {
  const typeMap = {
    'required': 'Required',
    'minLength': 'Min Length',
    'maxLength': 'Max Length',
    'pattern': 'Pattern/Regex',
    'email': 'Email Format',
    'number': 'Number',
    'min': 'Min Value',
    'max': 'Max Value',
    'url': 'URL Format',
    'date': 'Date Format',
    'custom': 'Custom'
  };

  return typeMap[type] || type;
};

/**
 * Extract field validations from content (JSON or Markdown)
 * @param {string} content - Content to parse
 * @returns {Object} - Object containing fields array and groups array
 */
export const extractFieldValidations = (content) => {
  try {
    console.log('Extracting field validations from content');

    // Try to parse as JSON first
    try {
      const parsedJson = JSON.parse(content);
      if (Array.isArray(parsedJson)) {
        console.log('Successfully parsed content as JSON array');
        // Add stable IDs to JSON fields if they don't have them
        const fieldsWithIds = parsedJson.map(field => {
          if (!field.id) {
            // Create a stable ID based on the field name/label
            const nameForId = field.label || field.name || 'unnamed';
            field.id = `field-${nameForId.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          }
          return field;
        });
        // Extract unique groups
        const groups = [...new Set(fieldsWithIds.map(field => field.group || 'General'))];
        return { fields: fieldsWithIds, groups };
      }
    } catch (jsonError) {
      console.log('Content is not valid JSON, parsing as markdown');
    }

    // If not JSON, extract structured data from markdown
    const fields = [];
    const groups = new Set(['General']);
    let currentCategory = 'General';

    // First, split by categories (## double hash)
    const categoryParts = content.split(/(?:^|\n)##\s+(?!#)/);

    // Process each category
    for (const categoryPart of categoryParts) {
      if (!categoryPart.trim()) continue;

      // Extract category name from first line
      const categoryLines = categoryPart.split('\n');
      const categoryFirstLine = categoryLines[0].trim();

      // Check if this is a category header (doesn't contain field markers)
      if (categoryFirstLine &&
          !categoryFirstLine.toLowerCase().includes('field validation') &&
          !categoryFirstLine.startsWith('###')) {
        // This is a category name
        currentCategory = categoryFirstLine;
        groups.add(currentCategory);
      }

      // Now split this category by field sections (### triple hash)
      const sections = categoryPart.split(/(?:^|\n)###\s+/);

      for (const section of sections) {
        if (!section.trim()) continue;

        // Basic field extraction from section
        const lines = section.split('\n');
        let fieldName = lines[0].trim();

        // Skip non-field sections or category headers
        if (fieldName.toLowerCase().includes('field validation') ||
            fieldName.toLowerCase() === 'validation rules' ||
            fieldName.toLowerCase() === 'implementation example' ||
            fieldName.toLowerCase() === 'ux considerations' ||
            fieldName === currentCategory ||
            !fieldName) {
          continue;
        }

      // Create a stable ID based on the field name/label rather than position
      const fieldId = `field-${fieldName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

      let fieldType = 'Text';
      let fieldLabel = fieldName;
      let fieldDescription = '';
      let fieldGroup = currentCategory; // Use the current category as the default group
      let fieldUsageContext = ''; // Store the usage context separately
      const validations = [];
      let implementation = '';
      let inValidationSection = false;
      let inImplementationSection = false;
      let currentValidation = null;

      // Extract field details
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();

        // Skip empty lines
        if (!line) continue;

        // Check if we're entering implementation section
        if (line.includes('Implementation Example') || line.includes('#### Implementation')) {
          inImplementationSection = false; // Will be set to true when we hit the code block
          inValidationSection = false;
          continue;
        }

        // Check for code block start
        if (line.match(/^```(javascript|js|typescript|ts)?$/)) {
          if (!inImplementationSection) {
            inImplementationSection = true;
            inValidationSection = false;
          } else {
            inImplementationSection = false;
          }
          continue;
        }

        // Capture implementation code (preserve original indentation)
        if (inImplementationSection) {
          implementation += lines[i] + '\n'; // Use original line with indentation
          continue;
        }

        // Check if we're entering validation rules section
        if (line.includes('Validation Rules') || line.includes('#### Validation')) {
          inValidationSection = true;
          continue;
        }

        // Parse field properties (handles both formats: "Name: X Type: Y" on same line or separate lines)
        if (line.match(/Name:\s*\w+/i)) {
          const nameMatch = line.match(/Name:\s*(\w+)/i);
          if (nameMatch) {
            fieldName = nameMatch[1].trim();
          }
        }

        if (line.match(/Type:\s*\w+/i)) {
          const typeMatch = line.match(/Type:\s*(\w+)/i);
          if (typeMatch) {
            fieldType = typeMatch[1].trim();
          }
        }

        if (line.match(/Group:\s*.+/i)) {
          const groupMatch = line.match(/Group:\s*(.+)/i);
          if (groupMatch) {
            // Store usage context but DON'T override the category from ## header
            fieldUsageContext = groupMatch[1].trim();
            // Only use Group if we don't have a category from ## header
            if (currentCategory === 'General') {
              fieldGroup = fieldUsageContext;
              groups.add(fieldGroup);
            }
          }
        }

        // Parse validation rules (handles bullet format: • Required or - Required or **Required**)
        if (inValidationSection) {
          // Check for value or error sub-properties first (these should update current validation)
          if (currentValidation && line.match(/^\s*[◦\-•*]?\s*value:\s*(.+)/i)) {
            const valueMatch = line.match(/^\s*[◦\-•*]?\s*value:\s*(.+)/i);
            let value = valueMatch[1].trim();

            // Parse numeric values
            if (!isNaN(value) && value !== 'Yes' && value !== 'No') {
              value = parseInt(value, 10);
            } else if (value.toLowerCase() === 'yes') {
              value = true;
            } else if (value.toLowerCase() === 'no') {
              value = false;
            }

            currentValidation.value = value;
          }
          // Check for error sub-property
          else if (currentValidation && line.match(/^\s*[◦\-•*]?\s*error:\s*(.+)/i)) {
            const errorMatch = line.match(/^\s*[◦\-•*]?\s*error:\s*(.+)/i);
            currentValidation.message = errorMatch[1].trim();
          }
          // Check for validation rule start (bullet points) - but NOT value/error lines
          else if (line.match(/^[•\-*]\s*\*?\*?(.+?)\*?\*?$/) &&
                   !line.match(/^\s*[◦\-•*]?\s*(value|error):/i)) {
            // Save previous validation if exists
            if (currentValidation && currentValidation.message) {
              validations.push(currentValidation);
            }

            // Extract validation type
            const typeMatch = line.match(/^[•\-*]\s*\*?\*?(.+?)\*?\*?$/);
            let validationType = typeMatch[1].trim();

            // Normalize validation type
            const typeMap = {
              'required': 'required',
              'min length': 'minLength',
              'minimum length': 'minLength',
              'max length': 'maxLength',
              'maximum length': 'maxLength',
              'pattern/regex': 'pattern',
              'pattern': 'pattern',
              'regex': 'pattern',
              'email format': 'email',
              'email': 'email',
              'duplicate check': 'unique',
              'unique': 'unique'
            };

            const normalizedType = typeMap[validationType.toLowerCase()] || validationType.toLowerCase();

            currentValidation = {
              type: normalizedType,
              value: true,
              message: ''
            };
          }
        }
      }

      // Save last validation if exists and has a message
      if (currentValidation && currentValidation.message) {
        validations.push(currentValidation);
      }

      // Only create field if we found validations or it has field properties
      if (validations.length > 0 || fieldName !== fieldLabel) {
        fields.push({
          id: fieldId,
          name: fieldName,
          label: fieldLabel,
          type: fieldType,
          description: fieldDescription,
          validations: validations,
          group: fieldGroup, // This is the category from ## header
          usageContext: fieldUsageContext, // This is from **Group:** field (where it's used)
          implementation: implementation.trim()
        });
      }
      } // End of field sections loop
    } // End of category parts loop

    console.log(`Extracted ${fields.length} fields from markdown`);
    console.log(`Found ${groups.size} categories:`, Array.from(groups));
    return { fields, groups: Array.from(groups) };
  } catch (error) {
    console.error('Error extracting field validations:', error);
    return { fields: [], groups: ['General'] };
  }
};

/**
 * Convert field object to editable markdown string (for editing - without header metadata)
 * @param {Object} field - Field object
 * @param {boolean} includeHeader - Whether to include header metadata (default: false for editing)
 * @returns {string} - Markdown formatted string
 */
export const fieldToMarkdown = (field, includeHeader = false) => {
  let content = '';

  // Only include header metadata when explicitly requested (for full document generation)
  if (includeHeader) {
    // Add field details - use ### for field name (since ## is for category)
    content += `### ${field.label || field.name}\n\n`;
    if (field.description) {
      content += `${field.description}\n\n`;
    }

    content += `**Name:** ${field.name}\n`;
    content += `**Type:** ${field.type}\n`;

    // Store the usage context if it exists, not the category
    if (field.usageContext) {
      content += `**Group:** ${field.usageContext}\n\n`;
    } else if (field.group && field.group !== 'General') {
      content += `**Group:** ${field.group}\n\n`;
    } else {
      content += `**Group:** General\n\n`;
    }
  }

  // Add validation rules - use #### for subsections (since ### is for field name)
  if (field.validations && field.validations.length > 0) {
    content += `#### Validation Rules\n\n`;
    field.validations.forEach(validation => {
      content += `- **${formatValidationType(validation.type)}**\n`;
      content += `  - Value: ${validation.value}\n`;
      content += `  - Error: ${validation.message}\n\n`;
    });
  }

  // Add implementation example if exists - use #### for subsections
  if (field.implementation) {
    content += `#### Implementation Example\n\n`;
    content += '```javascript\n';
    content += field.implementation;
    content += '\n```\n';
  }

  return content;
};

/**
 * Parse markdown string back to field object
 * @param {string} content - Markdown content
 * @param {string} originalId - Original field ID to preserve
 * @param {string} originalGroup - Original category/group to preserve
 * @returns {Object} - Field object
 */
export const markdownToField = (content, originalId, originalGroup = 'General') => {
  const field = {
    id: originalId,
    name: '',
    label: '',
    type: 'Text',
    description: '',
    group: originalGroup, // Preserve original category
    validations: [],
    implementation: '',
    usageContext: '' // Store usage context separately
  };

  // Parse the edited content
  const lines = content.split('\n');
  let inValidationSection = false;
  let inImplementationSection = false;
  let implementationLines = [];
  let currentValidation = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Parse field header (### Field Name)
    if (line.startsWith('###') && !line.startsWith('####')) {
      const headerMatch = line.match(/^###\s+(.+)$/);
      if (headerMatch) {
        field.label = headerMatch[1].trim();
      }
    }

    // Parse field properties
    if (line.startsWith('**Name:**')) {
      field.name = line.replace('**Name:**', '').trim();
    } else if (line.startsWith('**Type:**')) {
      field.type = line.replace('**Type:**', '').trim();
    } else if (line.startsWith('**Group:**')) {
      // Store as usageContext, DON'T overwrite the category (group)
      field.usageContext = line.replace('**Group:**', '').trim();
    } else if (!line.startsWith('**') && !line.startsWith('#') && line.length > 0 && !inValidationSection && !inImplementationSection && i > 0) {
      // This might be the description
      if (!lines[i - 1].startsWith('#') && field.description === '') {
        field.description = line;
      }
    }

    // Check for validation rules section (now uses ####)
    if (line.includes('#### Validation Rules') || line.includes('### Validation Rules')) {
      inValidationSection = true;
      inImplementationSection = false;
      continue;
    }

    // Check for implementation section (now uses ####)
    if (line.includes('#### Implementation Example') || line.includes('### Implementation Example')) {
      inValidationSection = false;
      inImplementationSection = true;
      continue;
    }

    // Parse validation rules
    if (inValidationSection && line.startsWith('- **')) {
      // Save previous validation if exists
      if (currentValidation) {
        field.validations.push(currentValidation);
      }

      // Start new validation
      const typeMatch = line.match(/- \*\*(.+?)\*\*/);
      if (typeMatch) {
        currentValidation = {
          type: typeMatch[1].toLowerCase().replace(/\s+/g, ''),
          value: true,
          message: ''
        };

        // Map display names back to type values
        const typeMap = {
          'required': 'required',
          'minlength': 'minLength',
          'maxlength': 'maxLength',
          'pattern/regex': 'pattern',
          'emailformat': 'email',
          'number': 'number',
          'minvalue': 'min',
          'maxvalue': 'max',
          'urlformat': 'url',
          'dateformat': 'date',
          'custom': 'custom'
        };

        const normalizedType = typeMatch[1].toLowerCase().replace(/[\s\/]/g, '');
        currentValidation.type = typeMap[normalizedType] || normalizedType;
      }
    } else if (inValidationSection && currentValidation && line.includes('- Value:')) {
      const valueMatch = line.match(/- Value:\s*(.+)/);
      if (valueMatch) {
        let value = valueMatch[1].trim();
        // Try to parse as number if applicable
        if (!isNaN(value) && value !== '') {
          value = parseInt(value, 10);
        }
        currentValidation.value = value;
      }
    } else if (inValidationSection && currentValidation && line.includes('- Error:')) {
      const errorMatch = line.match(/- Error:\s*(.+)/);
      if (errorMatch) {
        currentValidation.message = errorMatch[1].trim();
      }
    }

    // Parse implementation example
    if (inImplementationSection) {
      if (line.startsWith('```javascript')) {
        continue;
      } else if (line.startsWith('```')) {
        field.implementation = implementationLines.join('\n');
        implementationLines = [];
        inImplementationSection = false;
      } else {
        implementationLines.push(lines[i]); // Use original line with indentation
      }
    }
  }

  // Save last validation if exists
  if (currentValidation) {
    field.validations.push(currentValidation);
  }

  return field;
};

/**
 * Convert array of fields to full markdown content
 * @param {Array} fields - Array of field objects
 * @returns {string} - Full markdown content
 */
export const fieldsToMarkdown = (fields) => {
  // Group fields by category
  const groupedFields = {};
  fields.forEach(field => {
    const group = field.group || 'General';
    if (!groupedFields[group]) {
      groupedFields[group] = [];
    }
    groupedFields[group].push(field);
  });

  // Generate markdown with category headers
  let content = '';
  const categories = Object.keys(groupedFields);

  categories.forEach((category, categoryIndex) => {
    // Add category header
    content += `## ${category}\n\n`;

    // Add fields under this category
    const categoryFields = groupedFields[category];
    categoryFields.forEach((field, fieldIndex) => {
      // Include header metadata when generating full document
      content += fieldToMarkdown(field, true);

      // Add separator between fields (but not after last field in category)
      if (fieldIndex < categoryFields.length - 1) {
        content += '\n---\n\n';
      }
    });

    // Add spacing between categories (but not after last category)
    if (categoryIndex < categories.length - 1) {
      content += '\n\n';
    }
  });

  return content;
};

/**
 * Format field for copying to clipboard
 * @param {Object} field - Field object
 * @returns {string} - Formatted text
 */
export const formatFieldForCopy = (field) => {
  const esc = (str) => (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cleanVal = (v) => (v || '').toString().replace(/`/g, '');

  const label = field.label || field.name;

  // --- Build HTML version (for rich text paste) ---
  let html = `<b>${esc(label)}</b><br>`;
  if (field.description) {
    html += `<p>${esc(field.description)}</p>`;
  }

  // Field Details HTML table
  html += `<p><b>Field Details</b></p>`;
  html += `<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;">`;
  html += `<tr><th>Property</th><th>Value</th></tr>`;
  html += `<tr><td>Name</td><td>${esc(field.name || '')}</td></tr>`;
  html += `<tr><td>Type</td><td>${esc(field.type || 'Text')}</td></tr>`;
  html += `<tr><td>Group</td><td>${esc(field.group || 'General')}</td></tr>`;
  html += `</table>`;

  // Validation Rules HTML table
  html += `<p><b>Validation Rules</b></p>`;
  if (field.validations && field.validations.length > 0) {
    html += `<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;">`;
    html += `<tr><th>Type</th><th>Value</th><th>Error Message</th></tr>`;
    field.validations.forEach(validation => {
      html += `<tr>`;
      html += `<td>${esc(formatValidationType(validation.type))}</td>`;
      html += `<td>${esc(cleanVal(validation.value))}</td>`;
      html += `<td>${esc(validation.message || '')}</td>`;
      html += `</tr>`;
    });
    html += `</table>`;
  } else {
    html += `<p>No validation rules defined.</p>`;
  }

  if (field.implementation) {
    html += `<p><b>Implementation Example</b></p>`;
    html += `<pre>${esc(field.implementation)}</pre>`;
  }

  // --- Build plain text version (for plain text paste) ---
  const t = '\t';
  let text = `${label}\n\n`;
  if (field.description) {
    text += `${field.description}\n\n`;
  }
  text += `Field Details\n`;
  text += `Property${t}Value\n`;
  text += `Name${t}${field.name || ''}\n`;
  text += `Type${t}${field.type || 'Text'}\n`;
  text += `Group${t}${field.group || 'General'}\n\n`;
  text += `Validation Rules\n`;
  if (field.validations && field.validations.length > 0) {
    text += `Type${t}Value${t}Error Message\n`;
    field.validations.forEach(validation => {
      text += `${formatValidationType(validation.type)}${t}${cleanVal(validation.value)}${t}${validation.message || ''}\n`;
    });
  } else {
    text += `No validation rules defined.\n`;
  }
  if (field.implementation) {
    text += `\nImplementation Example\n${'-'.repeat(40)}\n${field.implementation}\n`;
  }

  return { html, text };
};
